import React from 'react';
import { ResponsiveContainer, Sankey, Tooltip } from 'recharts';
import DataTable from './DataTable';
import { BUN_CHART_FONT } from './chartTheme';

const formatValue = (value) => Number(value).toLocaleString();

function FlowNode({ x, y, width, height, payload, nodeDetail, nodeLeading }) {
  const source = !payload.sourceLinks?.length;
  const sink = !payload.targetLinks?.length;
  const edge = source || sink;
  const labelWidth = source ? 130 : 210;
  const labelX = source ? x - labelWidth - 12 : sink ? x + width + 12 : x + width / 2 - labelWidth / 2;
  const labelY = edge ? y + height / 2 - 36 : y - 76;
  const detail = nodeDetail?.(payload);
  const leading = nodeLeading?.(payload);
  return (
    <g fontFamily={BUN_CHART_FONT}>
      <rect x={x} y={y} width={width} height={height} fill={payload.color || 'var(--bun-data-violet)'} fillOpacity={0.9} />
      <foreignObject x={labelX} y={labelY} width={labelWidth} height={72}>
        <div className={`flex h-full items-center gap-2 font-instrument text-bun-text ${source ? 'justify-end text-right' : sink ? 'text-left' : 'justify-center text-center'}`}>
          {leading && <span className="flex shrink-0 items-center" aria-hidden="true">{leading}</span>}
          <div className="min-w-0 bg-bun-surface">
            <div title={payload.name} className="line-clamp-2 break-words text-bun-body font-semibold leading-5">{payload.name}</div>
            {detail != null && <div className="text-bun-meta font-medium leading-5 tabular-nums">{detail}</div>}
          </div>
        </div>
      </foreignObject>
    </g>
  );
}

function FlowLink({ sourceX, targetX, sourceY, targetY, sourceControlX, targetControlX, linkWidth, payload }) {
  return <path d={`M${sourceX},${sourceY} C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`} fill="none" stroke={payload?.color || payload?.target?.color || 'var(--bun-data-violet)'} strokeWidth={linkWidth} strokeOpacity={0.35} />;
}

/** Callers own graph preparation and domain units; this component owns presentation. */
export default function SankeyChart({ data, height = 520, ariaLabel = 'Flow chart', valueFormatter = formatValue, valueLabel = 'Value', nodeDetail, nodeLeading }) {
  if (!data?.nodes || data.nodes.length < 2 || !data.links?.length) return null;
  // Recharts keys edges by source, target and value. Coalesce parallel edges so
  // repeated transitions have one stable path and table row, without losing weight.
  const linksByPair = new Map();
  data.links.forEach(link => {
    const key = `${link.source}-${link.target}`;
    const existing = linksByPair.get(key);
    if (existing) existing.value += link.value;
    else linksByPair.set(key, { ...link });
  });
  const graph = linksByPair.size === data.links.length ? data : { ...data, links: [...linksByPair.values()] };
  // Reserve room for two-line labels even when most nodes share a column.
  const chartHeight = Math.max(height, (graph.nodes.length - 1) * 80 + 116);
  return (
    <div className="min-w-0 w-full font-instrument">
      <div className="hidden lg:block" role="img" aria-label={ariaLabel}>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <Sankey data={graph} nodePadding={64} nodeWidth={12} align="left" margin={{ top: 80, right: 230, bottom: 36, left: 150 }} node={<FlowNode nodeDetail={nodeDetail} nodeLeading={nodeLeading} />} link={<FlowLink />}>
            <Tooltip formatter={(value) => [valueFormatter(value), valueLabel]} contentStyle={{ background: 'var(--bun-ink)', border: 'none', borderRadius: 8, color: 'var(--bun-on-ink)', fontFamily: BUN_CHART_FONT }} itemStyle={{ color: 'var(--bun-on-ink)' }} />
          </Sankey>
        </ResponsiveContainer>
      </div>
      <div className="lg:sr-only">
        <DataTable mobileCards aria-label={`${ariaLabel} data`}>
          <DataTable.Head><DataTable.Row><DataTable.Header>From</DataTable.Header><DataTable.Header>To</DataTable.Header><DataTable.Header>{valueLabel}</DataTable.Header></DataTable.Row></DataTable.Head>
          <DataTable.Body>
            {graph.links.map(link => (
              <DataTable.Row key={`${link.source}-${link.target}`}>
                {[['From', graph.nodes[link.source]], ['To', graph.nodes[link.target]]].map(([label, node]) => (
                  <DataTable.Cell key={label} mobileLabel={label}>
                    <span className="flex items-center gap-2 text-bun-text">
                      {nodeLeading && <span className="shrink-0" aria-hidden="true">{nodeLeading(node)}</span>}
                      <span className="min-w-0"><span className="text-bun-body font-semibold">{node.name}</span>{nodeDetail && <span className="block text-bun-meta font-medium tabular-nums">{nodeDetail(node)}</span>}</span>
                    </span>
                  </DataTable.Cell>
                ))}
                <DataTable.Cell mobileLabel={valueLabel} mobilePrimary>{valueFormatter(link.value)}</DataTable.Cell>
              </DataTable.Row>
            ))}
          </DataTable.Body>
        </DataTable>
      </div>
    </div>
  );
}
